import { useCallback, useEffect, useState } from 'react';
import { WhatsAppApi } from '../../api/resources';
import { errorMessage, validationErrors } from '../../api/client';
import Modal from '../../components/Modal';
import ConfirmModal from '../../components/ConfirmModal';
import Icon from '../../components/Icon';

const STATUS_CLS = { APPROVED: 'badge badge-ok', REJECTED: 'badge badge-danger', PENDING: 'badge badge-plain' };

function CreateModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '',
    language: 'en_US',
    category: 'UTILITY',
    header_type: 'none',
    header_text: '',
    header_image: null,
    body: '',
    footer_text: '',
    examples: [],
  });
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const varCount = new Set([...form.body.matchAll(/\{\{(\d+)\}\}/g)].map((m) => m[1])).size;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setBanner('');
    try {
      await WhatsAppApi.createTemplate({
        name: form.name,
        language: form.language,
        category: form.category,
        header_type: form.header_type,
        header_text: form.header_type === 'text' ? form.header_text : null,
        header_image: form.header_type === 'image' ? form.header_image : null,
        body: form.body,
        footer_text: form.footer_text,
        body_examples: form.examples.slice(0, varCount),
      });
      onSaved();
    } catch (err) {
      setErrors(validationErrors(err));
      setBanner(errorMessage(err, 'Could not create the template.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New template"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Submitting…' : 'Submit to Meta'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}
      <form onSubmit={submit}>
        <div className="field">
          <label>Name * (lowercase letters, numbers, underscores)</label>
          <input type="text" value={form.name} onChange={set('name')} placeholder="welcome_message" />
          {errors.name && <div className="err">{errors.name}</div>}
        </div>
        <div className="field">
          <label>Language</label>
          <input type="text" value={form.language} onChange={set('language')} />
        </div>
        <div className="field">
          <label>Category</label>
          <select value={form.category} onChange={set('category')}>
            <option value="UTILITY">Utility</option>
            <option value="MARKETING">Marketing</option>
          </select>
        </div>
        <div className="field">
          <label>Header</label>
          <select value={form.header_type} onChange={set('header_type')}>
            <option value="none">None</option>
            <option value="text">Text</option>
            <option value="image">Image</option>
          </select>
        </div>
        {form.header_type === 'text' && (
          <div className="field">
            <input type="text" maxLength={60} value={form.header_text} onChange={set('header_text')} placeholder="Header text" />
          </div>
        )}
        {form.header_type === 'image' && (
          <div className="field">
            <input type="file" accept="image/jpeg,image/png" onChange={(e) => setForm({ ...form, header_image: e.target.files[0] || null })} />
            {errors.header_image && <div className="err">{errors.header_image}</div>}
          </div>
        )}
        <div className="field">
          <label>Body * (use {'{{1}}'}, {'{{2}}'} for variables)</label>
          <textarea rows={4} maxLength={1024} value={form.body} onChange={set('body')} />
          {errors.body && <div className="err">{errors.body}</div>}
        </div>
        {Array.from({ length: varCount }, (_, i) => (
          <div className="field" key={i}>
            <label>Sample value for {`{{${i + 1}}}`} *</label>
            <input
              type="text"
              value={form.examples[i] || ''}
              onChange={(e) => {
                const examples = [...form.examples];
                examples[i] = e.target.value;
                setForm({ ...form, examples });
              }}
            />
          </div>
        ))}
        {errors.body_examples && <div className="err">{errors.body_examples}</div>}
        <div className="field">
          <label>Footer</label>
          <input type="text" maxLength={60} value={form.footer_text} onChange={set('footer_text')} />
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

export default function TemplatesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [modal, setModal] = useState(null); // 'create' | { delete: tpl }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await WhatsAppApi.templates());
    } catch (err) {
      setError(errorMessage(err, 'Could not load templates.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    setError('');
    try {
      setRows(await WhatsAppApi.syncTemplates());
    } catch (err) {
      setError(errorMessage(err, 'Could not sync with Meta.'));
    } finally {
      setSyncing(false);
    }
  };

  const doDelete = async () => {
    const t = modal.delete;
    try {
      await WhatsAppApi.deleteTemplate(t.id);
      setModal(null);
      load();
    } catch (err) {
      setModal(null);
      setError(errorMessage(err, 'Could not delete template.'));
    }
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">WhatsApp templates</h1>
          <p className="page-sub">Pre-approved messages used to start conversations or reply after 24 hours.</p>
        </div>
        <div className="toolbar">
          <button className="btn" onClick={sync} disabled={syncing}>
            <Icon name="refresh" size={16} />
            {syncing ? 'Syncing…' : 'Sync status'}
          </button>
          <button className="btn btn-primary" onClick={() => setModal('create')}>
            <Icon name="plus" size={16} />
            New template
          </button>
        </div>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Body</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                rows.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <div className="name">{t.name}</div>
                      <div className="sub">{t.language}</div>
                    </td>
                    <td>{t.category}</td>
                    <td style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>{t.body}</td>
                    <td>
                      <span className={STATUS_CLS[t.status] || 'badge badge-plain'}>{t.status}</span>
                      {t.rejection_reason && <div className="sub">{t.rejection_reason}</div>}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="icon-btn danger" title="Delete" onClick={() => setModal({ delete: t })}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length === 0 && (
          <div className="empty">
            <strong>No templates yet</strong>
            Create one and submit it to Meta for approval.
          </div>
        )}
      </div>

      {modal === 'create' && (
        <CreateModal
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}
      {modal?.delete && (
        <ConfirmModal
          title="Delete template"
          message={`Delete “${modal.delete.name}” here and on WhatsApp? This cannot be undone.`}
          onConfirm={doDelete}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
